import Image from "next/image";
import Link from "next/link";

type CardItem = {
  icon: string;
  title?: string;
  description?: string | null;
  link_type?: "url" | "page" | string;
  link_url?: string;
  link_text?: string;
};

type CardGridSectionProps = {
  heading?: string;
  cards: CardItem[];
  columns?: number;
  card_style?: "default" | "bordered" | "elevated";
  add_background?: boolean;
  add_bottom_border?: boolean;
};

export default function CardGridSection({
  heading,
  cards,
  columns = 3,
  card_style = "default",
  add_background = false,
  add_bottom_border = false,
}: CardGridSectionProps) {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  }[columns] || "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

  // Helper function to get the correct icon source
  const getIconSrc = (icon: string) => {
    // If it's a full URL, use it directly
    if (icon.startsWith("http://") || icon.startsWith("https://")) {
      return icon;
    }
    // Otherwise, prepend /images/
    return `/images/${icon}`;
  };

  const cardStyleClass = {
    default: "bg-white",
    bordered: "bg-white border-2 border-gray-200",
    elevated: "bg-white shadow-lg",
  }[card_style];

  const content = (
    <>
      {heading && (
        <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-8">
          {heading}
        </h2>
      )}
      <div className={`grid ${gridCols} gap-6 ${add_bottom_border ? "pb-8 border-b border-gray-200" : ""}`}>
        {cards.map((card, index) => (
          <div
            key={index}
            className={`${cardStyleClass} rounded-2xl p-6 flex flex-col items-center text-center transition-transform hover:scale-105`}
          >
            <div className="mb-4">
              <Image
                src={getIconSrc(card.icon)}
                width={64}
                height={64}
                alt={card.title || "Card icon"}
                className="w-16 h-16"
              />
            </div>
            {card.title && (
              <h3 className="font-bold text-primary-700 text-lg mb-2">
                {card.title}
              </h3>
            )}
            {card.description && (
              <p className="text-gray-500 text-sm mb-4">{card.description}</p>
            )}
            {card.link_url && (
              <a
                href={card.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-block bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-800 transition-colors"
              >
                {card.link_text || "Learn More"}
              </a>
            )}
          </div>
        ))}
      </div>
    </>
  );

  if (add_background) {
    return (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[50vw] bg-gradient-to-b from-[rgba(237,237,249,0)] to-[#DEE5E8] pt-12 pb-12">
        <div
          className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-no-repeat bg-left-bottom bg-contain pointer-events-none"
          style={{
            backgroundImage: "url(/images/left-corner-supergrafis.png)",
          }}
        />
        <div className="container max-w-7xl mx-auto items-start gap-8 flex">
          <div className="w-80 shrink-0" />
          <div className="flex-1 flex flex-col">
            {content}
          </div>
        </div>
      </section>
    );
  }

  return <div className="flex flex-col">{content}</div>;
}
